import { useState } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import {
  useUpcomingSangamSessions,
  useAdminSangamControl,
  useCreateSangamSession,
  useDeleteSangamSession,
} from '@/hooks/useFirestore'
import { format } from 'date-fns'
import {
  Settings,
  Plus,
  Trash2,
  Loader2,
  Mic2,
  Play,
  Square,
  ExternalLink,
  RefreshCw,
} from 'lucide-react'

export default function AdminPage() {
  const { isAdmin, user } = useAuthStore()
  const { data: sangamSessions, isLoading: sangamLoading } = useUpcomingSangamSessions(10)
  const sangamControl = useAdminSangamControl()
  const createSangam = useCreateSangamSession()
  const deleteSangam = useDeleteSangamSession()

  const [showSangamForm, setShowSangamForm] = useState(false)
  const [sangamTopic, setSangamTopic] = useState('')
  const [sangamDate, setSangamDate] = useState('')
  const [sangamTime, setSangamTime] = useState('20:00')

  if (!isAdmin()) {
    return <Navigate to="/" replace />
  }

  const handleCreateSangam = async () => {
    if (!sangamTopic?.trim()) return
    try {
      let scheduledAt: Date
      if (sangamDate && sangamTime) {
        scheduledAt = new Date(`${sangamDate}T${sangamTime}`)
      } else {
        scheduledAt = new Date()
        scheduledAt.setMinutes(scheduledAt.getMinutes() + 2)
      }
      await createSangam.mutateAsync({
        topic: sangamTopic.trim(),
        scheduledAt,
      })
      setSangamTopic('')
      setSangamDate('')
      setSangamTime('20:00')
      setShowSangamForm(false)
    } catch (error) {
      console.error('Failed to create sangam:', error)
    }
  }

  return (
    <div className="space-y-8 animate-in">
      <header>
        <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Settings className="w-6 h-6 text-primary-500" />
          Admin Panel
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage Sangam podcast sessions
        </p>
      </header>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="card text-center">
          <Mic2 className="w-8 h-8 mx-auto text-primary-500 mb-2" />
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {sangamSessions?.length || 0}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Sessions</div>
        </div>
        <div className="card text-center">
          <Play className="w-8 h-8 mx-auto text-red-500 mb-2" />
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {sangamSessions?.filter(s => s.status === 'LIVE').length || 0}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Live Now</div>
        </div>
        <div className="card text-center">
          <Square className="w-8 h-8 mx-auto text-green-500 mb-2" />
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {sangamSessions?.filter(s => s.status === 'RECORDED').length || 0}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Recorded</div>
        </div>
      </div>

      {/* Sangam Management */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Mic2 className="w-5 h-5 text-primary-500" />
            Sangam Podcast Management
          </h2>
          <button
            onClick={() => setShowSangamForm(!showSangamForm)}
            className="btn-outline text-sm flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Schedule Session
          </button>
        </div>

        {/* Schedule Sangam Form */}
        {showSangamForm && (
          <div className="card mb-4 bg-primary-50 dark:bg-primary-900/10 border-primary-200 dark:border-primary-900/50 p-6">
            <h3 className="font-medium text-gray-900 dark:text-white mb-4">Schedule New Sangam Session</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Strategic Topic</label>
                <input
                  type="text"
                  value={sangamTopic}
                  onChange={(e) => setSangamTopic(e.target.value)}
                  placeholder="e.g. The future of AI governance"
                  className="input w-full"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date (optional)</label>
                  <input
                    type="date"
                    value={sangamDate}
                    onChange={(e) => setSangamDate(e.target.value)}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Time (Local, optional)</label>
                  <input
                    type="time"
                    value={sangamTime}
                    onChange={(e) => setSangamTime(e.target.value)}
                    className="input w-full"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowSangamForm(false)}
                  className="btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSangam}
                  disabled={createSangam.isPending || !sangamTopic?.trim()}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  {createSangam.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mic2 className="w-4 h-4" />
                  )}
                  Schedule Session
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          {sangamLoading ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading sessions...</div>
          ) : !sangamSessions || sangamSessions.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Mic2 className="w-8 h-8 mx-auto mb-2 text-primary-400 dark:text-primary-900/50" />
              <p>No upcoming sessions</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b dark:border-gray-800 text-left">
                    <th className="py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Topic</th>
                    <th className="py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Status / Phase</th>
                    <th className="py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Scheduled</th>
                    <th className="py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Participants</th>
                    <th className="py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-gray-800">
                  {sangamSessions.map((session) => (
                    <tr key={session.id}>
                      <td className="py-3 px-3">
                        <span className="font-medium dark:text-white">{session.topic}</span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase w-fit ${
                            session.status === 'LIVE'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 animate-pulse'
                              : session.status === 'PAUSED'
                              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : session.status === 'RECORDED'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          }`}>
                            {session.status}
                          </span>
                          {(session.status === 'LIVE' || session.status === 'PAUSED') && (
                            <span className="text-[10px] text-primary-500 font-bold uppercase tracking-tighter">
                              Phase: {session.phase || 'FOUNDATION'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-gray-600 dark:text-gray-400">
                        {session.scheduled_at ? format(session.scheduled_at, 'MMM d, h:mm a') : '—'}
                      </td>
                      <td className="py-3 px-3 text-gray-600 dark:text-gray-400">
                        {session.participants_count} / {session.max_participants || 5}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {session.status === 'WAITING' && (
                            <>
                              <button
                                onClick={() => sangamControl.mutate({ sessionId: session.id, moderatorApproved: true })}
                                disabled={sangamControl.isPending || (session.participants_count || 0) < 2}
                                className="btn-primary py-1 px-3 text-xs flex items-center gap-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                              >
                                <Play className="w-3 h-3" />
                                {sangamControl.isPending ? 'Starting...' : 'Go Ahead'}
                              </button>
                              <Link
                                to={`/?id=${session.id}`}
                                className="btn-outline py-1 px-3 text-xs flex items-center gap-1 border-primary-500 text-primary-500 hover:bg-primary-50"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Go to Podcast
                              </Link>
                            </>
                          )}
                          {(session.status === 'LIVE' || session.status === 'PAUSED') && (
                            <>
                              <Link
                                to={`/?id=${session.id}`}
                                className="btn-primary py-1 px-3 text-xs flex items-center gap-1 bg-primary-600"
                              >
                                <Play className="w-3 h-3" />
                                {session.status === 'PAUSED' ? 'View (Paused)' : 'View Live'}
                              </Link>
                              <button
                                onClick={() => sangamControl.mutate({ sessionId: session.id, action: 'pass_mic' })}
                                disabled={sangamControl.isPending}
                                className="btn-outline py-1 px-3 text-xs flex items-center gap-1 border-primary-500 text-primary-500 hover:bg-primary-50"
                              >
                                <RefreshCw className="w-3 h-3" />
                                Pass Mic
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to kill this podcast?')) {
                                    sangamControl.mutate({ sessionId: session.id, killSignal: true })
                                  }
                                }}
                                disabled={sangamControl.isPending}
                                className="btn-outline py-1 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50 flex items-center gap-1"
                              >
                                <Square className="w-3 h-3" />
                                Kill
                              </button>
                            </>
                          )}
                          {session.status === 'RECORDED' && (
                            <Link
                              to={`/?id=${session.id}`}
                              className="btn-outline py-1 px-3 text-xs flex items-center gap-1 border-primary-500 text-primary-500 hover:bg-primary-50"
                            >
                              <Play className="w-3 h-3" />
                              View Archive
                            </Link>
                          )}
                          <button
                            onClick={() => {
                              if (confirm(`Delete session "${session.topic}"? This cannot be undone.`)) {
                                deleteSangam.mutate(session.id)
                              }
                            }}
                            disabled={deleteSangam.isPending}
                            className="btn-outline py-1 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-900 dark:hover:bg-red-900/20 flex items-center gap-1"
                          >
                            {deleteSangam.isPending ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Trash2 className="w-3 h-3" />
                            )}
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Admin info */}
      <section className="card">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Admin Info</h3>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <p>
            <strong>Logged in as:</strong> {user?.email}
          </p>
          <p className="mt-2">
            <strong>How it works:</strong>
          </p>
          <ul className="ml-4 list-disc text-gray-500 dark:text-gray-500 space-y-1">
            <li>Schedule a new Sangam session with a strategic topic</li>
            <li>Agents auto-register as participants when the session is created</li>
            <li>Click "Go Ahead" to approve the moderator to start the podcast</li>
            <li>Use the live Sangam page for admin commands (pause, mute, redirect)</li>
            <li>View episode metrics and analytics after the podcast ends</li>
          </ul>
        </div>
      </section>
    </div>
  )
}
